import { useCallback, useMemo, useState } from 'react';

import { useInventoryCategoryTree } from '@atom/hooks/useInventoryCategoryTree';
import { Icon, IconButton } from '@atom/mui';
import { InventoryCategoryTree } from '@atom/types/inventory';
import {
  getCategoryById,
  getCategoryIdPath,
  getCategoryPathNames,
} from '@atom/utilities/categoryUtilities';

import ChatAssetDraftCategoryModal from './ChatAssetDraftCategoryModal';
import { useChatAssetDraftContext } from './ChatAssetDraftContext';

import classes from '../chat.module.css';

const ChatAssetDraftHeader = () => {
  const { schema, draftAsset, disabled, handleDraftAssetChange } =
    useChatAssetDraftContext();

  const categoryId = draftAsset?.categoryId ?? '';
  const [modalOpen, setModalOpen] = useState(false);

  const [categoryTree] = useInventoryCategoryTree({
    params: {
      schemaId: schema?.id,
      onlyPublished: true,
    },
  });

  const categoryPath = useMemo(() => {
    if (!categoryTree || !categoryId) {
      return null;
    }

    const idPath = getCategoryIdPath(categoryTree, categoryId);

    if (!idPath) {
      return null;
    }

    const names = getCategoryPathNames(categoryTree, idPath);

    return names.slice(1).join(' > ') || null;
  }, [categoryTree, categoryId]);

  const category = useMemo(() => {
    if (!categoryTree || !categoryId) {
      return null;
    }

    return getCategoryById(categoryId, [categoryTree]);
  }, [categoryTree, categoryId]);

  const handleCategoryClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleCategorySelect = useCallback(
    (selectedCategory: InventoryCategoryTree) => {
      handleDraftAssetChange({ categoryId: selectedCategory.id });
      setModalOpen(false);
    },
    [handleDraftAssetChange],
  );

  return (
    <div className={classes.assetDraftCardHeader}>
      <div className={classes.assetDraftCardHeaderRow}>
        <Icon className={classes.assetDraftCardHeaderIcon}>category</Icon>
        <span className={classes.assetDraftCardHeaderLabel}>
          Inventory Type
        </span>
        <span className={classes.assetDraftCardHeaderValue}>
          {schema?.name || 'Loading...'}
        </span>
      </div>
      <div className={classes.assetDraftCardHeaderRow}>
        <Icon className={classes.assetDraftCardHeaderIcon}>folder</Icon>
        <span className={classes.assetDraftCardHeaderLabel}>Category</span>
        <span className={classes.assetDraftCardHeaderValue}>
          {categoryPath || category?.name || 'Loading...'}
        </span>
        {!disabled && (
          <IconButton
            size="small"
            tooltip="Change category"
            onClick={handleCategoryClick}
          >
            <Icon>edit</Icon>
          </IconButton>
        )}
      </div>
      {schema && (
        <ChatAssetDraftCategoryModal
          open={modalOpen}
          schema={schema}
          categoryId={categoryId}
          categoryTree={categoryTree}
          onSelect={handleCategorySelect}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default ChatAssetDraftHeader;
